import React, { useEffect, useRef, useState } from "react";
import {
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Typography,
} from "@mui/material";
import AutoAwesomeIcon from "@mui/icons-material/AutoAwesome";
import RefreshIcon from "@mui/icons-material/Refresh";
import { toast } from "react-toastify";
import { buildArtifactHtml } from "./forecastArtifactHtml";
import { formatDateTime } from "../../utils/formatDate";
import { useDarkMode } from "../../components/context/darkModeContext";
import useForecastArtifact from "../planning-forecast/useForecastArtifact";
import { downloadSkuCoverageXlsx } from "../../utils/sku-coverage-xlsx-export";
import { downloadSkuArtifactXlsx } from "../../utils/inventory-ledger-xlsx-export";
import { buildSkuArtifactHtml } from "./skuArtifactHtml";
import { forecastDataService } from "../../utils/services/forecast-data.service";

/**
 * "Artifact" button: shows the full standalone report (charts + KPIs, richer
 * than the inline SKU Detail/Key Issues/Recommendations sections on the page
 * itself) for `productId` — or, when a single SKU is on screen (`skuRun`),
 * for that SKU only.
 *
 * The SKU report needs no generation of its own: it's built from that SKU's
 * run (forecast, method, reorder, ledger, warehouse split, and the SKU
 * insight/explanation the insight workflow already wrote), re-read when
 * opened so it carries the latest insight, plus the SKU's status and
 * recommendation from the product report when that one is ready.
 *
 * Shares live status with ProductArtifactSections via useForecastArtifact —
 * that component is the one that auto-generates on landing when nothing
 * exists yet (`autoGenerate: true` there, `false` here), so this button
 * spins and shows "Generating…" even for a run it didn't itself start,
 * and auto-opens once the shared state turns `ready`.
 */
const ForecastArtifactButton = ({ productId, skuRun, disabled, engine, clientId }) => {
  const { darkMode } = useDarkMode();
  const { artifact: state, generate } = useForecastArtifact(productId, {
    autoGenerate: false,
    engine,
    clientId,
  });
  const [openArtifact, setOpenArtifact] = useState(null); // { html, title, generatedAt, data, narrative }
  // True once the user has asked to see the report but it wasn't ready yet
  // — opens it automatically (or surfaces the error) as soon as it settles.
  const [wantsOpen, setWantsOpen] = useState(false);
  const mounted = useRef(true);
  const [skuLoading, setSkuLoading] = useState(false);

  useEffect(() => {
    mounted.current = true;
    return () => {
      mounted.current = false;
    };
  }, []);

  const openFromState = () => {
    if (!mounted.current || state.status !== "ready") return;
    setOpenArtifact({
      html: buildArtifactHtml({
        data: state.data,
        narrative: state.narrative,
        darkMode,
      }),
      title: state.data?.product?.name || "Product analysis",
      generatedAt: state.generated_at,
      data: state.data,
      narrative: state.narrative,
    });
  };

  useEffect(() => {
    if (!wantsOpen) return;
    if (state.status === "ready") {
      openFromState();
      setWantsOpen(false);
    } else if (state.status === "error") {
      toast.error(
        state.error || "Couldn't generate the artifact. Please try again.",
      );
      setWantsOpen(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [state, wantsOpen]);

  const openSku = async () => {
    setSkuLoading(true);
    let run = skuRun;
    try {
      // The latest copy: the insight may have landed after this page loaded.
      const res = await forecastDataService.get(skuRun._id);
      if (res?.data?._id) run = { ...skuRun, ...res.data };
    } catch (e) {
      // The copy on screen still makes a full report.
    }
    if (!mounted.current) return;
    setSkuLoading(false);
    if (run.insight_status === "pending") {
      toast.info("The DropSkip insight for this SKU is still being written; the report shows everything else.");
    }
    const productSku =
      state.status === "ready" ? (state.narrative?.skus || []).find((x) => x.sku === run.sku) : null;
    setOpenArtifact({
      kind: "sku",
      html: buildSkuArtifactHtml({ run, productSku, darkMode }),
      title: run.sku,
      generatedAt: run.createdAt,
      run,
    });
  };

  const handleClick = () => {
    if (skuRun?._id) {
      openSku();
      return;
    }
    if (!productId) return;
    if (state.status === "ready") {
      openFromState();
      return;
    }
    if (state.status === "pending" || state.status === "loading") {
      setWantsOpen(true); // already in flight — open it once it lands
      return;
    }
    setWantsOpen(true);
    generate();
  };

  // Regenerate from inside the open dialog — closes it (the button behind
  // it takes over showing "Generating…") and reopens automatically once the
  // fresh report lands.
  const regenerate = () => {
    setOpenArtifact(null);
    setWantsOpen(true);
    generate();
  };

  const download = () => {
    const url = URL.createObjectURL(
      new Blob([openArtifact.html], { type: "text/html" }),
    );
    const a = document.createElement("a");
    a.href = url;
    a.download = `${openArtifact.title.replace(/[^\w-]+/g, "_")}_analysis.html`;
    a.click();
    URL.revokeObjectURL(url);
  };

  const downloadExcel = () => {
    if (openArtifact.kind === "sku") {
      downloadSkuArtifactXlsx(openArtifact.run);
      return;
    }
    downloadSkuCoverageXlsx(
      openArtifact.data,
      openArtifact.narrative,
      `${openArtifact.title.replace(/[^\w-]+/g, "_")}_sku_coverage.xlsx`,
    );
  };

  const openInTab = () => {
    const url = URL.createObjectURL(
      new Blob([openArtifact.html], { type: "text/html" }),
    );
    window.open(url, "_blank", "noopener");
  };

  const isSku = Boolean(skuRun?._id);
  // A SKU report is built on the spot; only the product one is generated.
  const generating = !isSku && state.status === "pending";

  return (
    <>
      <Button
        variant="outlined"
        color="primary"
        disabled={disabled || (!isSku && !productId) || generating || skuLoading}
        onClick={handleClick}
        startIcon={
          generating || skuLoading ? (
            <CircularProgress size={16} color="inherit" />
          ) : (
            <AutoAwesomeIcon />
          )
        }
      >
        {generating ? "Generating…" : isSku ? "SKU Artifact" : "Artifact"}
      </Button>

      <Dialog
        open={!!openArtifact}
        onClose={() => setOpenArtifact(null)}
        fullWidth
        maxWidth="xl"
        PaperProps={{ sx: { height: "92vh" } }}
      >
        <DialogTitle sx={{ display: "flex", alignItems: "baseline", gap: 1.5 }}>
          {openArtifact?.title}
          {openArtifact?.generatedAt && (
            <Typography
              component="span"
              sx={{ fontSize: 12, fontWeight: 400, color: "text.secondary" }}
            >
              Generated {formatDateTime(openArtifact.generatedAt)}
            </Typography>
          )}
        </DialogTitle>
        <DialogContent dividers sx={{ p: 0 }}>
          {openArtifact && (
            <Box
              component="iframe"
              title={openArtifact.kind === "sku" ? "SKU analysis" : "Product analysis"}
              // No allow-same-origin: the report runs isolated from the app.
              sandbox="allow-scripts"
              srcDoc={openArtifact.html}
              sx={{ width: "100%", height: "100%", border: 0 }}
            />
          )}
        </DialogContent>
        <DialogActions>
          {openArtifact?.kind !== "sku" && (
            <Button startIcon={<RefreshIcon />} onClick={regenerate}>
              Regenerate
            </Button>
          )}
          <Button onClick={openInTab}>Open in new tab</Button>
          <Button onClick={download}>Download HTML</Button>
          <Button onClick={downloadExcel}>Download Excel</Button>
          <Button variant="contained" onClick={() => setOpenArtifact(null)}>
            Close
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default ForecastArtifactButton;
